(() => {
  const TIME_ZONE = 'America/Chicago';
  const OPEN_HOUR = 0;
  const CLOSE_HOUR = 15;
    const CLOSED_MESSAGE = "Today's Selection ordering is open Monday–Friday from 12:00 AM to 3:00 PM CT.";

  const chicagoHour = (date = new Date()) => {
    const hour = new Intl.DateTimeFormat('en-US', {
      timeZone: TIME_ZONE,
      hour: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .find((part) => part.type === 'hour')?.value;

    return Number(hour);
  };

  const orderingIsOpen = (date = new Date()) => {
    const hour = chicagoHour(date);
    const weekday = new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, weekday: 'short' }).format(date);
    return weekday !== 'Sat' && weekday !== 'Sun' && Number.isFinite(hour) && hour >= OPEN_HOUR && hour < CLOSE_HOUR;
  };

  const setText = (element, text) => {
    if (element.textContent !== text) element.textContent = text;
  };

  const rememberAndDisable = (control) => {
    if (!control.hasAttribute('data-order-window-was-disabled')) {
      control.dataset.orderWindowWasDisabled = String(control.disabled);
    }
    control.disabled = true;
    control.setAttribute('aria-disabled', 'true');
  };

  const restoreControl = (control) => {
    if (!control.hasAttribute('data-order-window-was-disabled')) return;
    control.disabled = control.dataset.orderWindowWasDisabled === 'true';
    if (!control.disabled) control.removeAttribute('aria-disabled');
    control.removeAttribute('data-order-window-was-disabled');
  };

  const setButtonLabel = (control, closed) => {
    const label = control.querySelector(':scope > span:first-child');
    if (!label) return;

    if (closed) {
      if (!label.hasAttribute('data-order-window-label')) {
        label.dataset.orderWindowLabel = label.textContent.trim();
      }
      setText(label, 'Ordering closed');
    } else if (label.hasAttribute('data-order-window-label')) {
      setText(label, label.dataset.orderWindowLabel);
      label.removeAttribute('data-order-window-label');
    }
  };

  const closeProductCard = (card) => {
    if (!card.hasAttribute('data-order-window-card-managed')) {
      card.dataset.orderWindowWasInert = String(card.hasAttribute('inert'));
      card.dataset.orderWindowHadAriaDisabled = String(card.hasAttribute('aria-disabled'));
      card.dataset.orderWindowAriaDisabled = card.getAttribute('aria-disabled') || '';
      card.setAttribute('data-order-window-card-managed', 'true');
    }

    card.classList.add('todays-selection-card--closed');
    card.setAttribute('inert', '');
    card.setAttribute('aria-disabled', 'true');
  };

  const restoreProductCard = (card) => {
    if (!card.hasAttribute('data-order-window-card-managed')) return;

    card.classList.remove('todays-selection-card--closed');
    card.toggleAttribute('inert', card.dataset.orderWindowWasInert === 'true');
    if (card.dataset.orderWindowHadAriaDisabled === 'true') {
      card.setAttribute('aria-disabled', card.dataset.orderWindowAriaDisabled);
    } else {
      card.removeAttribute('aria-disabled');
    }
    card.removeAttribute('data-order-window-was-inert');
    card.removeAttribute('data-order-window-had-aria-disabled');
    card.removeAttribute('data-order-window-aria-disabled');
    card.removeAttribute('data-order-window-card-managed');
  };

  const syncProductCards = (open) => {
    const liveShelfCards = new Set(
      [
        ...document.querySelectorAll(
          '.card-wrapper [data-order-type="live-shelf"], .card-wrapper [data-live-shelf-card-inventory]'
        ),
      ]
        .map((root) => root.closest('.card-wrapper'))
        .filter(Boolean)
    );

    document.querySelectorAll('[data-order-window-card-managed]').forEach((card) => {
      if (open || !liveShelfCards.has(card)) restoreProductCard(card);
    });

    if (!open) liveShelfCards.forEach(closeProductCard);
  };

  const syncOrderingControls = (open) => {
    document.querySelectorAll('[data-order-type="live-shelf"]').forEach((root) => {
      const controls = root.matches('button, input, select')
        ? [root]
        : [...root.querySelectorAll('button, input, select')];

      controls.forEach((control) => {
        if (open) restoreControl(control);
        else rememberAndDisable(control);
        if (control.matches('button[type="submit"], button[name="add"]')) {
          setButtonLabel(control, !open);
        }
      });
    });
  };

  const syncCartControls = (open) => {
    document.querySelectorAll('[data-live-shelf-cart="true"]').forEach((root) => {
      const controls = root.matches('button, input')
        ? [root]
        : [...root.querySelectorAll('button, input')];
      controls.forEach((control) => (open ? restoreControl(control) : rememberAndDisable(control)));
      root.toggleAttribute('inert', !open);
    });
  };

  const syncMessages = (open) => {
    document.querySelectorAll('[data-order-window-message]').forEach((message) => {
      message.hidden = open;
      setText(message, CLOSED_MESSAGE);
    });
  };

  let lastOpenState;
  let syncing = false;

  const millisecondsUntilStateChange = (date = new Date()) => {
    const startingTime = date.getTime();
    const startingState = orderingIsOpen(date);
    let lowerBound = startingTime;
    let upperBound = startingTime;

    for (let hour = 1; hour <= 96; hour += 1) {
      upperBound = startingTime + hour * 60 * 60 * 1000;
      if (orderingIsOpen(new Date(upperBound)) !== startingState) break;
      lowerBound = upperBound;
    }

    while (upperBound - lowerBound > 50) {
      const midpoint = lowerBound + Math.floor((upperBound - lowerBound) / 2);
      if (orderingIsOpen(new Date(midpoint)) === startingState) lowerBound = midpoint;
      else upperBound = midpoint;
    }

    return Math.max(0, upperBound - Date.now() + 25);
  };

  const sync = () => {
    if (syncing) return;
    syncing = true;
    const open = orderingIsOpen();
    document.documentElement.dataset.todaysSelectionOrdering = open ? 'open' : 'closed';
    syncProductCards(open);
    syncOrderingControls(open);
    syncCartControls(open);
    syncMessages(open);
    lastOpenState = open;
    syncing = false;
  };

  document.addEventListener(
    'submit',
    (event) => {
      if (orderingIsOpen()) return;
      const isLiveShelfAdd = event.target.closest?.('[data-order-type="live-shelf"]');
      const isLiveShelfCheckout = event.target.querySelector?.('[data-live-shelf-cart="true"]');
      if (!isLiveShelfAdd && !isLiveShelfCheckout) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      sync();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (orderingIsOpen()) return;
      if (!event.target.closest?.('[data-order-type="live-shelf"], [data-live-shelf-cart="true"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      sync();
    },
    true
  );

  const observer = new MutationObserver(() => queueMicrotask(sync));
  const scheduleStateChange = () => {
    window.setTimeout(() => {
      const open = orderingIsOpen();
      if (open !== lastOpenState) {
        window.location.reload();
        return;
      }

      sync();
      scheduleStateChange();
    }, millisecondsUntilStateChange());
  };

  const start = () => {
    sync();
    observer.observe(document.body, { childList: true, subtree: true });
    scheduleStateChange();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  window.todaysSelectionOrdering = { orderingIsOpen, sync };
})();
